/** What the user reads when the server refuses an upload. A refusal answers the same way to the same bytes,
 *  so none of these texts invites to try again. A multi-line text opens with «what happened» and closes with
 *  «what to do», one line each (a «why» line may sit between), split by line breaks — surfaces render it with
 *  `whitespace-pre-line`. */
export const UPLOAD_REJECTION_MESSAGES = {
  EXTENSION_NOT_ALLOWED: 'Файл такого формата загрузить нельзя\nЗагрузите JPEG, PNG или WebP',
  CONTENT_MISMATCH:
    'Формат не поддерживается\n' +
    'Файл назван как фото, но на самом деле это другой формат, переименованный в изображение\n' +
    'Экспортируйте файл в JPEG, PNG или WebP и загрузите снова',
  REJECTED: 'Файл не загрузился',
} as const;

/**
 * The upload guards (`assertUploadAllowed`, `assertImageContentMatchesExtension`) reject with a bare
 * `BadRequestException(message)` — no errorCode — so a reason is recognised by its message template.
 * Each pattern is anchored at the template start and matches the phrase after the closing quote of the
 * file name, so a file name that merely contains the phrase cannot fake a reason.
 */
const KNOWN_REJECTIONS: ReadonlyArray<readonly [pattern: RegExp, message: string]> = [
  [/^The file extension "[^"]*" is not allowed for upload process "/, UPLOAD_REJECTION_MESSAGES.EXTENSION_NOT_ALLOWED],
  [/^File "[\s\S]*" has no extension\. Upload process "[^"]*" accepts: [^"]*$/, UPLOAD_REJECTION_MESSAGES.EXTENSION_NOT_ALLOWED],
  [/^The file "[\s\S]*" is named as a \w+ image, but its content is a /, UPLOAD_REJECTION_MESSAGES.CONTENT_MISMATCH],
];

/** A direct NestJS response carries the text in `message`; the global HttpExceptionFilter in `error`. */
function rejectionText(body: unknown): string {
  if (typeof body === 'string') return body;
  if (typeof body !== 'object' || body === null) return '';
  const { message, error } = body as { message?: unknown; error?: unknown };
  if (typeof message === 'string') return message;
  return typeof error === 'string' ? error : '';
}

/** 4xx other than timeout (408) and rate limit (429): the server gave its verdict, a repeat gets the same one. */
export function isUploadRejectionStatus(status: number): boolean {
  return status >= 400 && status < 500 && status !== 408 && status !== 429;
}

/** The user-facing reason of an upload refusal, from the error response body. The server text itself is
 *  never returned: it is English and speaks in internal terms (upload process names). */
export function describeUploadRejection(body: unknown): string {
  const text = rejectionText(body);
  return KNOWN_REJECTIONS.find(([pattern]) => pattern.test(text))?.[1] ?? UPLOAD_REJECTION_MESSAGES.REJECTED;
}
